/** Pure local edit-buffer for the Takt Editor — the SINGLE place its staged
 *  edits live. UI-ONLY for this iteration: nothing here is persisted or written
 *  to the backend; Save is a no-op. Every editor interaction funnels through one
 *  of these pure functions (take the buffer, return the next), so the change
 *  count and undo stack stay coherent. No React, no IO — unit-testable directly.
 *
 *  Edits store only true diffs: setting a value back to its committed/original
 *  clears the entry, so `changeCount` reflects real pending changes.
 */

/** A per-activity overlay: its work code, label, color, and placement. Only the
 *  fields that differ from the committed activity are stored. */
export interface ActivityPatch {
  code?: string;
  name?: string;
  color?: string;
  section?: string;
  takt_a?: string;
  takt_b?: string;
  takt_c?: string;
}

/** The committed values an ActivityPatch is diffed against. */
export interface ActivityCommitted {
  code: string;
  name: string;
  color: string;
  section: string;
  takt_a: string;
  takt_b: string;
  takt_c: string;
}

/** Where a dragged activity lands when dropped on a tree node. */
export interface MoveTarget {
  section: string;
  takt_a: string;
  takt_b: string;
  takt_c: string;
}

/** One activity's move, as applied in a (possibly bulk) drop. */
export interface ActivityMove {
  id: number;
  patch: ActivityPatch;
  committed: ActivityCommitted;
}

/** A board-wide work-code edit from the Work Codes tab, keyed by `code|prefix`. */
export interface CodeEdit {
  newCode?: string;
  name?: string;
  color?: string;
}

/** A trade staged for creation — `tempId` is NEGATIVE (never collides with a
 *  real registry id); commit creates the trade and maps assignments onto the
 *  real id. */
export interface TradeDraft {
  tempId: number;
  name: string;
  fill_color: string;
  font_color: string;
}

/** Staged edits to an EXISTING trade (rename / recolor). */
export interface TradePatch {
  name?: string;
  fill_color?: string;
  font_color?: string;
}

export interface TaktEdits {
  /** Inline section renames, keyed by the original Takt_SectionName. */
  sectionNames: Record<string, string>;
  /** Inline zone (Takt_B) renames, keyed by `${section}|${takt_b}`. */
  zoneNames: Record<string, string>;
  /** Inline level (Takt_C) renames, keyed by `${section}|${takt_b}|${takt_c}`. */
  levelNames: Record<string, string>;
  /** Per-activity code/color/placement overlay, keyed by activity id. */
  activityEdits: Record<number, ActivityPatch>;
  /** Board-wide work-code edits, keyed by `${code}|${prefix}`. */
  codeEdits: Record<string, CodeEdit>;
  /** Staged trade re-assignments, keyed by `${code}|${prefix}|${section}` (the
   *  source/committed code+prefix, since the backend maps off committed Takt
   *  fields). Trades are per-section, so a code can carry a different trade in
   *  each section it appears in. The value may be a TradeDraft's negative
   *  tempId. `null` = unassign that section; absent = unchanged. */
  tradeEdits: Record<string, number | null>;
  /** Staged trade CRUD — like everything else, nothing hits the API until
   *  the save commits. */
  tradesCreated: TradeDraft[];
  tradesUpdated: Record<number, TradePatch>;
  tradesDeleted: number[];
  /** Section display order (names). Empty = as loaded. */
  sectionOrder: string[];
  /** Per-section zone (Takt_B) order — the Takt ordering of zones. */
  zoneOrder: Record<string, string[]>;
  /** Per-zone level (Takt_C) order, keyed `${section}|${takt_b}`. */
  levelOrder: Record<string, string[]>;
}

export function emptyEdits(): TaktEdits {
  return {
    sectionNames: {},
    zoneNames: {},
    levelNames: {},
    activityEdits: {},
    codeEdits: {},
    tradeEdits: {},
    tradesCreated: [],
    tradesUpdated: {},
    tradesDeleted: [],
    sectionOrder: [],
    zoneOrder: {},
    levelOrder: {},
  };
}

export const zoneKey = (section: string, taktB: string): string => `${section}|${taktB}`;
export const levelKey = (section: string, taktB: string, taktC: string): string =>
  `${section}|${taktB}|${taktC}`;

/** Total pending changes — drives the "N changes" pill and the dirty check. */
export function changeCount(e: TaktEdits): number {
  return (
    Object.keys(e.sectionNames).length +
    Object.keys(e.zoneNames).length +
    Object.keys(e.levelNames).length +
    Object.keys(e.activityEdits).length +
    Object.keys(e.codeEdits).length +
    Object.keys(e.tradeEdits).length +
    e.tradesCreated.length +
    Object.keys(e.tradesUpdated).length +
    e.tradesDeleted.length +
    (e.sectionOrder.length > 0 ? 1 : 0) +
    Object.keys(e.zoneOrder).length +
    Object.keys(e.levelOrder).length
  );
}

const norm = (v: string | undefined): string => (v ?? "").trim();

/** Set `key` to `value` in `map`, or drop it when `value` matches `original`
 *  (a rename back to the source is "no change", never a stored diff). */
function setDiff(
  map: Record<string, string>,
  key: string,
  value: string,
  original: string,
): Record<string, string> {
  const next = { ...map };
  if (norm(value) === norm(original) || !norm(value)) delete next[key];
  else next[key] = value;
  return next;
}

export function renameSection(e: TaktEdits, original: string, value: string): TaktEdits {
  return { ...e, sectionNames: setDiff(e.sectionNames, original, value, original) };
}

export function renameZone(
  e: TaktEdits,
  section: string,
  taktB: string,
  value: string,
): TaktEdits {
  return { ...e, zoneNames: setDiff(e.zoneNames, zoneKey(section, taktB), value, taktB) };
}

export function renameLevel(
  e: TaktEdits,
  section: string,
  taktB: string,
  taktC: string,
  value: string,
): TaktEdits {
  return {
    ...e,
    levelNames: setDiff(e.levelNames, levelKey(section, taktB, taktC), value, taktC),
  };
}

const PATCH_FIELDS: (keyof ActivityPatch)[] = [
  "code",
  "name",
  "color",
  "section",
  "takt_a",
  "takt_b",
  "takt_c",
];

/** Apply one activity's edit form. Merges `patch` over any prior staged edit,
 *  keeping only fields that differ from `committed`; drops the whole entry when
 *  nothing differs (a move/recode back to the source clears it). */
export function editActivity(
  e: TaktEdits,
  activityId: number,
  patch: ActivityPatch,
  committed: ActivityCommitted,
): TaktEdits {
  const merged: ActivityPatch = { ...(e.activityEdits[activityId] ?? {}), ...patch };
  const diff: ActivityPatch = {};
  for (const f of PATCH_FIELDS) {
    const v = merged[f];
    if (v === undefined) continue;
    if (norm(v) !== norm(committed[f])) diff[f] = v;
  }
  const next = { ...e.activityEdits };
  if (Object.keys(diff).length === 0) delete next[activityId];
  else next[activityId] = diff;
  return { ...e, activityEdits: next };
}

/** Apply several activity moves as ONE edit (a bulk drag = one undo step). */
export function editActivities(e: TaktEdits, moves: ActivityMove[]): TaktEdits {
  return moves.reduce((acc, m) => editActivity(acc, m.id, m.patch, m.committed), e);
}

/** Delete a zone outright: untag every activity still in it (the `moves` carry
 *  them back to the untagged rail) AND drop any pending rename for the zone or
 *  its levels — so the zone vanishes on commit and a later zone of the same name
 *  can't inherit a stale rename. Applied as ONE undo step. The empty in-session
 *  shell is removed by the caller (local structure state, not the edit buffer). */
export function deleteZone(
  e: TaktEdits,
  section: string,
  taktB: string,
  moves: ActivityMove[],
): TaktEdits {
  const untagged = editActivities(e, moves);
  const zk = zoneKey(section, taktB);
  const levelPrefix = zk + "|";
  const zoneNames = { ...untagged.zoneNames };
  delete zoneNames[zk];
  const levelNames: Record<string, string> = {};
  for (const [k, v] of Object.entries(untagged.levelNames)) {
    if (!k.startsWith(levelPrefix)) levelNames[k] = v;
  }
  return { ...untagged, zoneNames, levelNames };
}

/** Delete one level outright: untag its activities and drop any pending rename
 *  for it. One undo step; the caller drops the empty shell from local state. */
export function deleteLevel(
  e: TaktEdits,
  section: string,
  taktB: string,
  taktC: string,
  moves: ActivityMove[],
): TaktEdits {
  const untagged = editActivities(e, moves);
  const lk = levelKey(section, taktB, taktC);
  const levelNames = { ...untagged.levelNames };
  delete levelNames[lk];
  return { ...untagged, levelNames };
}

/** Upsert-merge a board-wide work-code edit, keyed by `code|prefix`. */
export function editWorkCode(
  e: TaktEdits,
  code: string,
  prefix: string,
  patch: CodeEdit,
): TaktEdits {
  const key = `${code}|${prefix}`;
  const merged: CodeEdit = { ...(e.codeEdits[key] ?? {}), ...patch };
  // Store exactly what was typed so a controlled input tracks it perfectly
  // (no live-trim/empty-drop, which would yank the field mid-type). A field is
  // dropped only when it reverts to the source value (revert = no edit).
  if (merged.newCode === code) delete merged.newCode;
  if (merged.name === prefix) delete merged.name;
  const next = { ...e.codeEdits };
  if (merged.newCode === undefined && merged.name === undefined && merged.color === undefined) {
    delete next[key];
  } else {
    next[key] = merged;
  }
  return { ...e, codeEdits: next };
}

/** Merge several work codes into one: recode every source onto the target
 *  (code + name + color), so they collapse to a single code everywhere. The
 *  source equal to the target just adopts the (possibly renamed) name/color. */
export function mergeWorkCodes(
  e: TaktEdits,
  sources: { code: string; prefix: string }[],
  target: { code: string; name: string; color: string },
): TaktEdits {
  return sources.reduce(
    (acc, s) =>
      editWorkCode(acc, s.code, s.prefix, {
        newCode: target.code,
        name: target.name,
        color: target.color,
      }),
    e,
  );
}

/** One staged trade change: a `code|prefix|section` key, the target trade
 *  (`null` = unassign), and that key's current (server) trade. */
export interface TradeEditEntry {
  key: string;
  tradeId: number | null;
  current: number | null;
}

/** Stage trade (re-)assignments by section-scoped key. Setting a key back to
 *  its current trade clears the entry (revert = no edit), so `changeCount`
 *  stays honest. The caller builds the keys (whole-code = one per section;
 *  remove-from-section = a single key → null). */
export function setWorkCodeTrade(e: TaktEdits, entries: TradeEditEntry[]): TaktEdits {
  if (entries.length === 0) return e;
  const next = { ...e.tradeEdits };
  for (const { key, tradeId, current } of entries) {
    if (tradeId === (current ?? null)) delete next[key];
    else next[key] = tradeId;
  }
  return { ...e, tradeEdits: next };
}

/** Stage a new trade. Returns the buffer + the draft's tempId (negative,
 *  unique within the buffer) so the caller can e.g. assign codes to it. */
export function createTradeDraft(
  e: TaktEdits,
  draft: { name: string; fill_color: string; font_color: string },
): TaktEdits {
  const tempId = -(e.tradesCreated.reduce((m, t) => Math.max(m, -t.tempId), 0) + 1);
  return { ...e, tradesCreated: [...e.tradesCreated, { tempId, ...draft }] };
}

/** Stage a rename/recolor. A staged-new trade (negative id) edits its draft
 *  in place; an existing trade stores only the fields that differ from its
 *  server values — reverting every field drops the entry. */
export function updateTradeDraft(
  e: TaktEdits,
  tradeId: number,
  patch: TradePatch,
  current: { name: string; fill_color: string; font_color: string },
): TaktEdits {
  if (tradeId < 0) {
    return {
      ...e,
      tradesCreated: e.tradesCreated.map((t) =>
        t.tempId === tradeId ? { ...t, ...patch } : t,
      ),
    };
  }
  const merged: TradePatch = { ...(e.tradesUpdated[tradeId] ?? {}), ...patch };
  const diff: TradePatch = {};
  for (const f of ["name", "fill_color", "font_color"] as const) {
    const v = merged[f];
    if (v !== undefined && v !== current[f]) diff[f] = v;
  }
  const next = { ...e.tradesUpdated };
  if (Object.keys(diff).length === 0) delete next[tradeId];
  else next[tradeId] = diff;
  return { ...e, tradesUpdated: next };
}

/** Stage a trade deletion. A staged-new trade just disappears from the draft;
 *  an existing one is queued for deletion on save. Either way, staged
 *  assignments pointing at it are dropped (its codes read as unassigned). */
export function deleteTradeDraft(e: TaktEdits, tradeId: number): TaktEdits {
  const tradeEdits: Record<string, number | null> = {};
  for (const k of Object.keys(e.tradeEdits)) {
    if (e.tradeEdits[k] !== tradeId) tradeEdits[k] = e.tradeEdits[k];
  }
  if (tradeId < 0) {
    return {
      ...e,
      tradeEdits,
      tradesCreated: e.tradesCreated.filter((t) => t.tempId !== tradeId),
    };
  }
  const tradesUpdated = { ...e.tradesUpdated };
  delete tradesUpdated[tradeId];
  return {
    ...e,
    tradeEdits,
    tradesUpdated,
    tradesDeleted: e.tradesDeleted.includes(tradeId)
      ? e.tradesDeleted
      : [...e.tradesDeleted, tradeId],
  };
}

export function reorderSections(e: TaktEdits, order: string[]): TaktEdits {
  return { ...e, sectionOrder: order };
}

export function reorderZones(e: TaktEdits, section: string, order: string[]): TaktEdits {
  return { ...e, zoneOrder: { ...e.zoneOrder, [section]: order } };
}

export function reorderLevels(
  e: TaktEdits,
  section: string,
  taktB: string,
  order: string[],
): TaktEdits {
  return { ...e, levelOrder: { ...e.levelOrder, [zoneKey(section, taktB)]: order } };
}
